// Webrtc plugin.
import { mediaActions } from '../interface/actions'
import * as actionTypes from '../interface/actionTypes'
import watchMediaEvents from './media'

// Libraries.
import { put, takeEvery, fork } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'

/**
 * Sets up event listeners for Media Manager events. The events are turned into
 *    actions and dispatched to be handled by redux elsewhere.
 * @method watchMediaManagerEvents
 * @param  {Object} manager The Media Manager.
 */
export default function * watchMediaManagerEvents (manager) {
  /**
   * Create an event channel between the Media Manager and redux-saga.
   * This pushes its events into the redux-saga context.
   */
  const managerChannel = eventChannel(emit => {
    return setListeners(manager, emit)
  })

  yield takeEvery(managerChannel, mediaManagerEventsHandler)
}

function * mediaManagerEventsHandler ({ media, action }) {
  // If it is a new Media, watch for its events.
  if (action.type === actionTypes.MEDIA_NEW) {
    yield fork(watchMediaEvents, media)
  }

  // Dispatch the action
  yield put(action)
}

/**
 * Function to set-up listeners for the Media Manager events.
 * Contains the logic for how a webRTC event should be parsed
 *    before being sent elsewhere. Split into a stand-alone
 *    function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   manager The Media Manager.
 * @param  {Function} emit    Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (manager, emit, END = 'END') {
  const newMedia = id => {
    const media = manager.get(id)
    /**
     * When a new media has been added,
     * update redux state's webrtc.media array by adding in the new media object
     * (List of tracks will just be a list of track ids).
     */
    emit({
      media,
      action: mediaActions.newMedia(id, {
        local: media.getState().isLocal,
        tracks: media.getTracks().map(track => track.id)
      })
    })
  }

  const removedMedia = id => {
    /**
     * When a media has been removed,
     * update redux state's webrtc.media array
     * by removing the media with the specified media id.
     */
    emit({
      action: mediaActions.removedMedia(id)
    })
  }

  manager.on('media:new', newMedia)
  manager.on('media:removed', removedMedia)

  const unsubscribe = () => {
    manager.off('media:new', newMedia)
    manager.off('media:removed', removedMedia)
  }
  return unsubscribe
}
